import { CoreState } from "./core.state";

type Selector<T> = (state: CoreState) => T;
type Callback<T> = (value: T) => void;

/**
 * This can be used to subscribe to specific parts of the store so callback is not executed when unrelated state changes occur.
 * It also allows for deep comparisons when needed.
 * USAGE: const unsubscribe = subscriptionManager.subscribe(
      coreStore,
      state => state.aiPanel?.isOpen, (example selector)
      setIsOpen (example callback)
    );
 */
class SubscriptionManager {

  subscribe<T>(
    store: any,
    selector: Selector<T>,
    callback: Callback<T>,
    equalityFn: (a: T, b: T) => boolean = Object.is
  ) {
    let previousValue = selector(store.getState());

    const unsubscribe = store.subscribe(() => {
      const currentValue = selector(store.getState());

      if (!equalityFn(previousValue, currentValue)) {
        previousValue = currentValue;
        callback(currentValue);
      }
    });

    return unsubscribe;
  }

  // For deep object comparisons
  subscribeDeep<T>(
    store: any,
    selector: Selector<T>,
    callback: Callback<T>
  ) {
    return this.subscribe(
      store,
      selector,
      callback,
      (a, b) => JSON.stringify(a) === JSON.stringify(b)
    );
  }
}

export const subscriptionManager = new SubscriptionManager();